import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  Input,
  Button,
  Typography,
  Empty,
  Tag,
  Upload,
  message,
  Progress,
} from "antd";
import {
  Search,
  Plus,
  Image as ImageIcon,
  Video,
  FilePlus,
  Check,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LiteAPI from "../api/liteAPI";

const FILE_SIZE_LIMIT = 1 * 1024 * 1024 * 1024; // 1 GB

const ACCEPTED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
]);

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const { Text } = Typography;
const { Dragger } = Upload;

/**
 * LibraryPickerModal — An inline modal that lets users browse their
 * content library and add items to the playlist without leaving the page.
 * Also supports quick uploads directly from this modal.
 */
const LibraryPickerModal = ({
  visible,
  onClose,
  libraryList,
  setLibraryList,
  onAddToPlaylist,
  apiProps,
  brandData,
  refreshLibrary,
  hasScreen = true,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [accountStorage, setAccountStorage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const blockedFiles = useRef(new Set());

  // storageUsed is derived from libraryList — updates instantly on add/delete
  const storageUsed = useMemo(
    () => libraryList.reduce((sum, f) => sum + (Number(f.sizeBytes) || 0), 0),
    [libraryList],
  );
  const storageTotal = accountStorage?.storagebyte?.totalspace ?? null;

  const refreshAccountStorage = () => {
    if (!apiProps || !brandData?.BrandID) return;
    LiteAPI.getAccountStorage(brandData.BrandID, apiProps.usertoken)
      .then((data) => {
        if (data) setAccountStorage(data);
      })
      .catch((err) =>
        console.warn("[LibraryPickerModal] Storage fetch failed:", err),
      );
  };

  useEffect(() => {
    if (visible) refreshAccountStorage();
  }, [visible, brandData?.BrandID]);

  const filteredList = libraryList.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleToggle = (item) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
  };

  const handleDone = () => {
    const allSelected = libraryList.filter((item) => selectedIds.has(item.id));
    if (allSelected.length > 0) {
      onAddToPlaylist(allSelected); // pass full array — let caller handle bulk
    }
    onClose();
    setSearchQuery("");
    setSelectedIds(new Set());
  };

  // Quick upload from within the modal
  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => onSuccess("ok"), 400);
  };

  const beforeUpload = (file) => {
    if (!ACCEPTED_MIME_TYPES.has(file.type)) {
      message.error(
        `"${file.name}" is not a supported file type. Please upload JPG, PNG, GIF, or MP4 files.`,
      );
      blockedFiles.current.add(file.uid);
      return false;
    }
    if (file.size > FILE_SIZE_LIMIT) {
      message.error(`"${file.name}" exceeds the maximum file size of 1 GB`);
      blockedFiles.current.add(file.uid);
      return false;
    }
    if (storageTotal !== null) {
      if (storageUsed + file.size > storageTotal) {
        const remaining = storageTotal - storageUsed;
        message.error(
          `Insufficient storage space — Remaining ${formatBytes(remaining)}, File requires ${formatBytes(file.size)}`,
        );
        blockedFiles.current.add(file.uid);
        return false;
      }
    }
    return true;
  };

  const handleUpload = async (info) => {
    if (info.file.status === "uploading") return;

    if (blockedFiles.current.has(info.file.uid)) {
      blockedFiles.current.delete(info.file.uid);
      return;
    }

    const file = info.file.originFileObj || info.file;

    if (apiProps && brandData) {
      try {
        setUploadProgress(0);
        await LiteAPI.uploadFile(
          file,
          brandData.BrandID,
          apiProps,
          (percent) => {
            setUploadProgress(percent);
          },
        );
        setUploadProgress(null);
        message.success(`"${file.name}" uploaded successfully.`);
        // Refresh library — storageUsed updates automatically from libraryList
        if (refreshLibrary) await refreshLibrary();
      } catch (err) {
        setUploadProgress(null);
        message.error(`Upload failed for "${file.name}".`);
        console.error("Upload error:", err);
      }
    } else if (info.file.status === "done") {
      const newFile = {
        id: "lib-" + Date.now().toString(),
        name: info.file.name,
        type:
          info.file.type && info.file.type.includes("video")
            ? "video"
            : "image",
        duration: "10s",
        size: (info.file.size / (1024 * 1024)).toFixed(1) + " MB",
        uploadedAt: "Just now",
        url: info.file.originFileObj
          ? URL.createObjectURL(info.file.originFileObj)
          : null,
        previewUrl: info.file.originFileObj
          ? URL.createObjectURL(info.file.originFileObj)
          : null,
      };
      setLibraryList((prev) => [newFile, ...prev]);
      message.success(`"${info.file.name}" added to library.`);
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={() => {
        onClose();
        setSearchQuery("");
        setSelectedIds(new Set());
      }}
      footer={null}
      width={680}
      centered
      bodyStyle={{
        padding: 0,
        maxHeight: "80vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Text
          strong
          style={{
            fontSize: "18px",
            display: "block",
            marginBottom: "4px",
            color: "#0f172a",
          }}
        >
          Add from Library
        </Text>
        <Text type="secondary" style={{ fontSize: "13px" }}>
          Select items to add to your playlist, or upload new files.
        </Text>

        {/* Search + Upload */}
        <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
          <Input
            placeholder="Search files..."
            prefix={<Search size={16} color="#94a3b8" />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, borderRadius: "8px" }}
            allowClear
          />
          <Upload
            name="file"
            multiple={true}
            accept=".jpg,.jpeg,.png,.gif,.mp4"
            customRequest={apiProps && brandData ? undefined : dummyRequest}
            beforeUpload={beforeUpload}
            onChange={handleUpload}
            showUploadList={false}
          >
            <Button
              icon={<FilePlus size={14} color="#6366f1" />}
              style={{
                borderRadius: "8px",
                borderColor: "#c7d2fe",
                background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                color: "#6366f1",
                fontWeight: 500,
              }}
            >
              Upload
            </Button>
          </Upload>
        </div>
      </div>

      {/* Content Grid */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 24px" }}>
        {filteredList.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            {libraryList.length === 0 ? (
              <>
                <Dragger
                  name="file"
                  multiple={true}
                  accept=".jpg,.jpeg,.png,.gif,.mp4"
                  customRequest={
                    apiProps && brandData ? undefined : dummyRequest
                  }
                  beforeUpload={beforeUpload}
                  onChange={handleUpload}
                  showUploadList={false}
                  style={{
                    borderRadius: "16px",
                    border: "2px dashed #cbd5e1",
                    background: "#fafbfc",
                    padding: "28px 0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "14px",
                        background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FilePlus size={28} color="#6366f1" />
                    </div>
                    <Text strong style={{ fontSize: "15px", color: "#0f172a" }}>
                      Drop files here or click to upload
                    </Text>
                    <Text type="secondary" style={{ fontSize: "13px" }}>
                      JPG, PNG, GIF, MP4 • Max 1 GB per file •{" "}
                      {storageTotal !== null
                        ? `${formatBytes(storageUsed)} / ${formatBytes(storageTotal)} used`
                        : `${formatBytes(storageUsed)} used`}
                    </Text>
                    {uploadProgress !== null && (
                      <Progress
                        percent={uploadProgress}
                        size="small"
                        style={{ width: "200px", marginTop: "4px" }}
                      />
                    )}
                  </div>
                </Dragger>
              </>
            ) : (
              <Empty description="No files match your search" />
            )}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: "12px",
            }}
          >
            <AnimatePresence>
              {filteredList.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    style={{
                      background: "#ffffff",
                      border: isSelected
                        ? "2px solid #6366f1"
                        : "1px solid #f1f5f9",
                      borderRadius: "12px",
                      overflow: "hidden",
                      cursor: hasScreen ? "pointer" : "not-allowed",
                      transition: "all 0.2s ease",
                      position: "relative",
                      opacity: hasScreen ? 1 : 0.55,
                    }}
                    onClick={() => hasScreen && handleToggle(item)}
                    whileHover={
                      hasScreen
                        ? { y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }
                        : {}
                    }
                  >
                    {/* Thumbnail */}
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "16/9",
                        background: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {item.type === "video" ? (
                        <video
                          src={item.previewUrl || item.url}
                          muted
                          preload="metadata"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : item.previewUrl ? (
                        <img
                          src={item.previewUrl}
                          alt={item.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <ImageIcon size={28} color="#94a3b8" />
                      )}

                      {/* Type badge */}
                      <span
                        style={{
                          position: "absolute",
                          top: "6px",
                          left: "6px",
                          padding: "2px 8px",
                          borderRadius: "6px",
                          fontSize: "10px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          background:
                            item.type === "video"
                              ? "rgba(59, 130, 246, 0.9)"
                              : "rgba(236, 72, 153, 0.9)",
                          color: "#ffffff",
                        }}
                      >
                        {item.type === "video" ? "VID" : "IMG"}
                      </span>

                      {/* Add overlay */}
                      {!hasScreen ? (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(15, 23, 42, 0.45)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              background: "rgba(255,255,255,0.92)",
                              color: "#64748b",
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          >
                            Pair a screen first
                          </div>
                        </div>
                      ) : isSelected ? (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(99, 102, 241, 0.65)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Check size={32} color="#ffffff" strokeWidth={3} />
                        </div>
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(15, 23, 42, 0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s ease",
                          }}
                          className="picker-hover-overlay"
                        >
                          <div
                            style={{
                              padding: "8px 16px",
                              borderRadius: "8px",
                              background: "rgba(255,255,255,0.95)",
                              color: "#0f172a",
                              fontSize: "12px",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <Plus size={14} /> Add
                          </div>
                        </div>
                      )}
                    </div>

                    {/* File info */}
                    <div style={{ padding: "10px 12px" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginBottom: "2px",
                        }}
                      >
                        {item.name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                        {item.size} • {item.duration}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Footer with count + storage */}
      <div
        style={{
          padding: "12px 24px",
          borderTop: "1px solid #f1f5f9",
          background: "#fafbfc",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {libraryList.length} files in library
          </Text>
          {storageTotal !== null && (
            <Text type="secondary" style={{ fontSize: "11px" }}>
              {formatBytes(storageUsed)} / {formatBytes(storageTotal)} used
              {" — "}
              <span
                style={{
                  color:
                    storageTotal - storageUsed < 0.1 * storageTotal
                      ? "#ef4444"
                      : "#10b981",
                  fontWeight: 500,
                }}
              >
                {formatBytes(storageTotal - storageUsed)} left
              </span>
            </Text>
          )}
          {uploadProgress !== null && (
            <Progress
              percent={uploadProgress}
              size="small"
              style={{ width: "180px", marginTop: "2px" }}
            />
          )}
        </div>
        <Button
          type="primary"
          onClick={handleDone}
          style={{
            borderRadius: "8px",
            background: selectedIds.size > 0 ? "#6366f1" : "#94a3b8",
            borderColor: selectedIds.size > 0 ? "#6366f1" : "#94a3b8",
            fontWeight: 600,
            minWidth: 90,
          }}
        >
          {selectedIds.size > 0 ? `Add (${selectedIds.size})` : "Done"}
        </Button>
      </div>
    </Modal>
  );
};

export default LibraryPickerModal;
